import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { DashboardPinIcon } from '../assets/dashboard/DashboardPinIcon';
import { ReactComponent as FlipIcon } from '../assets/tools/icon-flip_projec-active.svg';
import { ReactComponent as ArvIcon } from '../assets/tools/icon-arv_analysis_active.svg';
import { ReactComponent as RentCompsIcon } from '../assets/dashboard/icon-rentcomps-default.svg';
import { ReactComponent as EditContactIcon } from '../assets/dashboard/edit-contact.svg';
import { ReactComponent as AddContactIcon } from '../assets/dashboard/add-contact.svg';
import { ReactComponent as TrashIcon } from '../assets/dashboard/trash-contact.svg';
import { ReactComponent as RoofIcon } from '../assets/dashboard/roof.svg';
import { ReactComponent as AcIcon } from '../assets/dashboard/ventilation.svg';
import { ReactComponent as WaterHeaterIcon } from '../assets/dashboard/water-heater.svg';
import { ReactComponent as FurnaceIcon } from '../assets/dashboard/fireplace.svg';
import { ReactComponent as ListedSaleIcon } from '../assets/dashboard/listed-for-sale.svg';
import { ReactComponent as ListingRemovedIcon } from '../assets/dashboard/listing-removed.svg';
import { ReactComponent as SoldIcon } from '../assets/dashboard/sold.svg';
import { ReactComponent as ListedRentIcon } from '../assets/dashboard/listed-for-rent.svg';
import { ReactComponent as IncreaseIcon } from '../assets/dashboard/price-increased.svg';
import { ReactComponent as DecreaseIcon } from '../assets/dashboard/price-decreased.svg';
import { ReactComponent as WarningIcon } from '../assets/dashboard/warning-red.svg';
import { SliderBackIcon } from '../assets/dashboard/SliderBackIcon';
import { SliderNextIcon } from '../assets/dashboard/SliderNextIcon';
import AppNavbar from '../features/AppNavbar';
import PageShell from '../layouts/PageShell';
import { reilmAPI } from '../services/apiClient';
import { STAGE_OPTIONS, STRATEGY_OPTIONS } from '../features/pipeline/options';

const ROLE_OPTIONS = [
  { value: 'seller', label: 'Seller' },
  { value: 'agent', label: 'Agent' },
  { value: 'attorney', label: 'Attorney' },
  { value: 'lender', label: 'Lender' },
  { value: 'other', label: 'Other' },
];

const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  multi_family: 'Multi Family',
  condo: 'Condo',
  townhouse: 'Townhouse',
};

const STATUS_ICON_MAP = {
  'Listed':     ListedSaleIcon,
  'Listing removed': ListingRemovedIcon,
  'Sold':            SoldIcon,
  "Listed for rent":     ListedRentIcon,
  price_increase:  IncreaseIcon,
  price_decrease:  DecreaseIcon,
};

const STATUS_LABEL_MAP = {
  listed_sale:     'Listed for Sale',
  listing_removed: 'Withdrawn',
  sold:            'Sold',
  listed_rent:     'Listed for Rent',
  price_increase:  'Price Increased',
  price_decrease:  'Price Decreased',
};

const STATUS_BADGE_BG = {
  listed_sale:     '#D85263',
  listing_removed: null,
  sold:            null,
  listed_rent:     '#F5C312',
  price_increase:  '#CBDCC3',
  price_decrease:  '#CBDCC3',
};

const STAGE_OPTIONS_WITH_LABEL = STAGE_OPTIONS.map(g => ({
  ...g,
  options: g.options.map(o => ({ ...o, stageLabel: g.label })),
}));
const ALL_STAGE_OPTIONS = STAGE_OPTIONS_WITH_LABEL.flatMap(g => g.options);

// Color scale matches assets/arv/school-ratings-color.svg
// 0=gray · 1–4=mid-green · 5–7=dark-green · 8–10=brand-dark
const getSchoolRatingColor = (rate) => {
  const n = parseInt(rate, 10);
  if (!n || n <= 0) return '#C4CCBE';
  if (n <= 4)       return '#4F7A35';
  if (n <= 7)       return '#2F4A1F';
  return '#17270D';
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatHistoryDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPrice = (price) => {
  if (!price && price !== 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
};

function phoneFormatEval(n) {
  if (n === undefined || n === '') return null
  if (n.length === 10) {
    const d = n.split('')
    return `(${d[0]}${d[1]}${d[2]}) ${d[3]}${d[4]}${d[5]}-${d[6]}${d[7]}${d[8]}${d[9]}`
  }
  return n.replace(/\D/g, '')
}

const selectStyles = {
  control: (base) => ({
    ...base,
    border: 'transparent',
    borderRadius: 8,
    boxShadow: 'none',
    minHeight: 40,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: 'white',
    '&:hover': { borderColor: '#4CBC18' },
  }),
  container: (base) => ({ ...base, right: '10px' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#E7F0E2' : state.isFocused ? '#f8fef4' : '#fff',
    color: '#17270D',
    fontSize: 13,
    cursor: 'pointer',
  }),
  groupHeading: (base) => ({
    ...base,
    fontSize: 11,
    fontWeight: 700,
    color: '#17270D',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({ ...base, caretColor: 'transparent' }),
};

const inputCls = 'w-full text-[13px] font-semibold text-center border border-brand-muted rounded-[4px] px-[12px] py-[8px] bg-white outline-none focus:border-brand-green hover:border-brand-green';
const labelCls = 'block text-[11px] text-brand-unselectedGray uppercase tracking-wide mb-[6px]';
const labelSectionCls = 'block text-[14px] font-bold text-brand-unselectedGray uppercase tracking-wide mb-[14px]';

const tabStyle = (active) => ({
  paddingBottom: 10,
  borderBottom: active ? '2px solid #4CBC18' : '2px solid transparent',
  fontWeight: active ? 700 : 400,
  color: active ? '#17270D' : '#A7C199',
  fontSize: 15,
  background: 'none',
  cursor: 'pointer',
  outline: 'none',
  whiteSpace: 'nowrap',
});

const roleStyle = (val) => ({
  position: 'relative',
  right: '10px'
});

const PropertyDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyTab, setHistoryTab] = useState(false);
  const [contactEditMode, setContactEditMode] = useState(false);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [contactIndex, setContactIndex] = useState(0);
  const [contacts, setContacts] = useState([]);

  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [stage, setStage] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesModified, setNotesModified] = useState(null);
  const [roofYear, setRoofYear] = useState('');
  const [acYear, setAcYear] = useState('');
  const [whYear, setWhYear] = useState('');
  const [furnaceYear, setFurnaceYear] = useState('');
  const [pinned, setPinned] = useState(false);

  const [cName, setCName] = useState('');
  const [cRole, setCRole] = useState(null);
  const [cMobile, setCMobile] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cOther, setCOther] = useState('');
  const [openRoleMenu, setOpenRoleMenu] = useState(false);

  useEffect(() => {
    if (!openRoleMenu) return;
    const close = () => setOpenRoleMenu(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openRoleMenu]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reilmAPI.getProperty(id)
      .then(({ data }) => {
        if (cancelled) return;
        const p = data.data.property;
        setProperty(p);
        setContacts(p.contact ?? []);
        setBeds(p.details?.beds ?? '');
        setBaths(p.details?.baths ?? '');
        setSqft(p.details?.sqft ?? '');
        setStage(ALL_STAGE_OPTIONS.find(o => o.value === p.stage) ?? null);
        setStrategy(STRATEGY_OPTIONS.flatMap(g => g.options).find(o => o.value === p.strategy) ?? null);
        setNotes(p.notes?.text ?? '');
        setNotesModified(p.notes?.updatedAt ?? null);
        setPinned(p.pin ?? false);
        setRoofYear(p.details?.roof ?? '');
        setAcYear(p.details?.ac ?? '');
        setWhYear(p.details?.water_heater ?? '');
        setFurnaceYear(p.details?.furnace ?? '');
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const save = (patch) => reilmAPI.updatedPropertiesDetails(id, patch).catch(() => {});

  const openEditContact = () => {
    const c = contacts[contactIndex];
    if (!c) return;
    setCName(c.name ?? '');
    setCRole(ROLE_OPTIONS.find(o => o.value === c.role) ?? null);
    setCMobile(c.mobile ?? '');
    setCEmail(c.email ?? '');
    setCOther(c.other ?? '');
    setIsAddingContact(false);
    setContactEditMode(true);
  };

  const openAddContact = () => {
    setCName(''); setCRole(null); setCMobile(''); setCEmail(''); setCOther('');
    setIsAddingContact(true);
    setContactEditMode(true);
  };

  const cancelContactEdit = () => {
    setContactEditMode(false);
    setIsAddingContact(false);
  };

  const handleContactSave = async () => {
    const updated = { name: cName, role: cRole?.value ?? null, mobile: cMobile, email: cEmail, other: cOther };
    let next;
    if (isAddingContact) {
      next = [...contacts, updated];
    } else {
      next = contacts.map((c, i) => i === contactIndex ? updated : c);
    }
    setContacts(next);
    if (isAddingContact) setContactIndex(next.length - 1);
    setContactEditMode(false);
    setIsAddingContact(false);
    await save({ contact: next });
  };

  const handleContactDelete = async () => {
    const next = contacts.filter((_, i) => i !== contactIndex);
    setContacts(next);
    setContactIndex(prev => Math.max(0, Math.min(prev, next.length - 1)));
    setContactEditMode(false);
    setIsAddingContact(false);
    await save({ contact: next });
  };

  const addressDisplay = property ? [
    property.address?.street,
    property.address?.city,
    property.address?.state,
    property.address?.zip,
  ].filter(Boolean).join(', ') : '';

  const groupedHistory = {};
  (property?.history ?? []).forEach(entry => {
    const year = entry.date ? new Date(entry.date).getFullYear() : 'Unknown';
    if (!groupedHistory[year]) groupedHistory[year] = [];
    groupedHistory[year].push(entry);
  });
  const sortedYears = Object.keys(groupedHistory).sort((a, b) => b - a);

  if (loading) {
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-brand-muted text-[20px]">Loading…</div>
      </PageShell>
    );
  }

  if (!property) {
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-red-400 text-[14px]">Property not found.</div>
      </PageShell>
    );
  }

  const details = property.details ?? {};
  const schools = details.schools ?? {};
  const currentContact = contacts[contactIndex];
  const hoaArray    = details.amenities?.homeowners_association ?? []
  const hoaFeeEntry  = hoaArray.find(s => s.startsWith('Association Fee:'))
  const hoaFreqEntry = hoaArray.find(s => s.startsWith('Association Fee Frequency:'))
  const hoaFeeAmt    = hoaFeeEntry  ? Number(hoaFeeEntry.split(':')[1]?.trim())  : null
  const hoaFreq      = hoaFreqEntry ? hoaFreqEntry.split(':')[1]?.trim()         : null
  const hoaAmenities = details.amenities?.amenities_and_community_features?.[0] ?? null
  const hasHoa       = (hoaFeeAmt != null && hoaFeeAmt > 0) || hoaAmenities != null

  return (
    <PageShell>
      <AppNavbar />
      <div className=" mt-[24px] flex flex-col gap-y-[20px] pb-[60px]">

        {/* Header */}
        <div className="flex flex-col gap-y-[8px]">
          <button
            onClick={() => navigate('/pipeline')}
            className="self-start text-[13px] font-medium text-brand-green hover:opacity-70 transition-opacity"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-[20px] font-bold text-brand-dark">{addressDisplay}</h1>
            <button
              onClick={() => { const next = !pinned; setPinned(next); save({ pin: next }) }}
              className="hover:opacity-80 transition-opacity"
              title={pinned ? 'Unpin property' : 'Pin property'}
            >
              <DashboardPinIcon active={pinned} />
            </button>
          </div>
        </div>
        

        {/* Analysis Tools row */}
        <div className="bg-white rounded-[8px] border border-[#DCEBD4] px-[24px] pt-[8px] pb-[24px]">
          <p className="py-[16px] text-[16px] font-extrabold text-brand-dark">Analysis Tools</p>
          <div className="grid grid-cols-3 gap-[24px]">

            {/* Flip & BRRRR */}
            <div className="rounded-[12px] p-[6px]" style={{ background: '#17270D' }}>
              <div
                className="rounded-[8px] h-[72px] px-[16px] flex items-center gap-[10px] border"
                style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}
              >
                <FlipIcon style={{ width: 36, height: 36, flexShrink: 0 }} />
                <div>
                  <p className="text-white font-bold text-[16px] leading-[16px]">Flip & BRRRR</p>
                  <p className="text-white text-[13px] leading-[16px] mt-[4px]">Projection</p>
                </div>
              </div>
            </div>

            {/* ARV Analysis */}
            <div
              className="rounded-[12px] p-[6px] cursor-pointer hover:opacity-90 transition-opacity"
              style={{ background: '#18290E' }}
              onClick={() => navigate(`/property/${id}/arv`, { state: { property } })}
            >
              <div
                className="rounded-[8px] h-[72px] px-[16px] flex items-center gap-[10px] border"
                style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}
              >
                <ArvIcon style={{ width: 36, height: 36, flexShrink: 0 }} />
                <div>
                  <p className="text-white font-bold text-[16px] leading-[16px]">ARV Analysis</p>
                  <p className="text-white text-[13px] leading-[16px] mt-[4px]">Valuation</p>
                </div>
              </div>
            </div>

            {/* Rent Comps */}
            <div className="rounded-[12px] p-[6px]" style={{ background: '#18290E' }}>
              <div
                className="rounded-[8px] h-[72px] px-[16px] flex items-center gap-[10px] border"
                style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}
              >
                <RentCompsIcon style={{ width: 36, height: 36, flexShrink: 0 }} />
                <div>
                  <p className="text-white font-bold text-[16px] leading-[16px]">Rent Comps</p>
                  <p className="text-white text-[13px] leading-[16px] mt-[4px]">Valuation</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Main content row */}
        <div className="flex gap-x-[16px] items-start">

          {/* Left: Property Details card */}
          <div className="basis-[57.5%] h-[674px] bg-white border border-[#DCEBD4] rounded-[8px] p-[28px]">

            {/* Tab bar */}
            <div className="flex items-end gap-x-[24px] mb-[24px]">
              <button className='border-b-2 border-brand-green ' style={tabStyle(!historyTab)} onClick={() => setHistoryTab(false)}>
                Property Details
              </button>
              <button style={tabStyle(historyTab)} onClick={() => setHistoryTab(true)}>
                History
              </button>
            </div>

            {/* Property Details tab */}
            {!historyTab && (
              <div className="flex flex-col gap-y-[28px]">

                {/* Features — editable inputs */}
                <div>
                  <p className={labelSectionCls}>Features</p>
                  <div className="grid grid-cols-3 gap-x-[12px]">
                    <div>
                      <label className={labelCls} style={{display:'flex', gap:'38px'}}>
                        <span>
                          Beds
                        </span>
                        <span>
                          Baths  
                        </span>
                        <span>
                          Sq.Ft
                        </span>
                      </label>
                      <div className="flex items-center gap-x-[6px]">
                        <input
                          type="number"
                          className={inputCls}
                          value={beds}
                          onChange={e => setBeds(e.target.value)}
                          onBlur={() => save({ 'details.beds': beds === '' ? null : Number(beds) })}
                          placeholder="Bd"
                        />
                        <input
                          type="number"
                          className={inputCls}
                          value={baths}
                          onChange={e => setBaths(e.target.value)}
                          onBlur={() => save({ 'details.baths': baths === '' ? null : Number(baths) })}
                          placeholder="Ba"
                        />
                        <input
                          type="number"
                          className={inputCls}
                          value={sqft}
                          onChange={e => setSqft(e.target.value)}
                          onBlur={() => save({ 'details.sqft': sqft === '' ? null : Number(sqft) })}
                          placeholder="Sqft"
                        />
                      </div>
                    </div>
                    {/* Col 2 — Stage: Status */}
                    <div>
                      <label className={labelCls}>Stage</label>
                      <Select
                        value={stage}
                        options={STAGE_OPTIONS_WITH_LABEL}
                        onChange={(opt) => { setStage(opt); save({ stage: opt?.value ?? null }); }}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isSearchable={false}
                        placeholder="Select "
                      />
                    </div>
                    {/* Col 3 — Strategy */}
                    <div>
                      <label className={labelCls}>Strategy</label>
                      <Select
                        value={strategy}
                        options={STRATEGY_OPTIONS}
                        onChange={(opt) => { setStrategy(opt); save({ strategy: opt?.value ?? null }); }}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isSearchable={false}
                        placeholder="Select "
                      />
                    </div>
                  </div>
                </div>

                {/* Features — 3-col read-only grid */}
                <div className="grid grid-cols-3 gap-x-[24px] gap-y-[20px]">
                  <div>
                    <span className={labelCls}>Property Type</span>
                    <p className="text-[13px] text-brand-dark font-semibold">{PROPERTY_TYPE_LABELS[property.propertyType] ?? property.propertyType ?? ''}</p>
                  </div>
                  <div>
                    <span className={labelCls}>Year Built</span>
                    <p className="text-[13px] text-brand-dark font-semibold">{details.yearBuilt ?? ''}</p>
                  </div>
                  <div>
                    <span className={labelCls}>Lot Size</span>
                    <p className="text-[13px] text-brand-dark font-semibold">{details.lotSize ? `${Number(details.lotSize).toLocaleString()} Acres` : ''}</p>
                  </div>
                  <div>
                    <span className={labelCls}>Flood Zone</span>
                    <div className="flex items-center gap-x-[6px]">
                      <p className="text-[13px] text-brand-dark font-semibold">{details.floodZone ?? ''}</p>
                      {details.floodZone && details.floodZone !== 'X' && details.floodZone.toLowerCase() !== 'none'}
                    </div>
                  </div>
                  <div>
                    <span className={labelCls}>Parking</span>
                    <p className="text-[13px] text-brand-dark font-semibold">{details.parking ?? ''}</p>
                  </div>
                </div>

                {/* HOA */}
                <div>
                  <p className={labelSectionCls}>HOA</p>
                  <div className="grid grid-cols-3 gap-x-[24px]">
                    <div>
                      <span className={labelCls}>HOA Fee</span>
                      <p className="text-[13px] text-brand-dark font-semibold">
                        {hoaFeeAmt != null && hoaFeeAmt > 0
                          ? `${formatPrice(hoaFeeAmt)}${hoaFreq ? `/${hoaFreq}` : ''}`
                          : ''}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className={labelCls}>Amenities</span>
                      <p className="text-[13px] text-brand-dark font-semibold">
                        {hoaAmenities }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Schools */}
                {(schools.elementary || schools.middle || schools.high) && (
                  <div>
                    <p className={labelSectionCls}>Schools</p>
                    <div className="grid grid-cols-3 gap-x-[16px]">
                      {[
                        { level: 'Elementary School', name: schools.elementary, rate: schools.elementary_state_rate },
                        { level: 'Middle School', name: schools.middle, rate: schools.middle_state_rate },
                        { level: 'High School', name: schools.high, rate: schools.high_state_rate },
                      ].map(({ level, name, rate }) => (
                        <div key={level}>
                          <span className={labelCls}>{level}</span>
                          <div className="flex items-center gap-x-[8px] mt-[6px]">
                            {rate != null && (
                              <span
                                className="w-[44px] h-[44px] rounded-full flex items-center justify-center text-white font-bold shrink-0"
                                style={{ backgroundColor: getSchoolRatingColor(rate) }}
                              >
                                <span style={{ fontSize: 15 }}>{rate}</span>
                                <span style={{ fontSize: 10, fontWeight: 400 }}>/10</span>
                              </span>
                            )}
                            <p className="text-[13px] text-brand-dark font-semibold">{name || ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Taxes */}
                <div>
                  <p className={labelSectionCls}>Taxes</p>
                  <div className="grid grid-cols-3 gap-x-[24px]">
                    <div>
                      <span className={labelCls}>Tax Year</span>
                      <p className="text-[13px] text-brand-dark font-semibold">{details.tax?.year ?? ''}</p>
                    </div>
                    <div>
                      <span className={labelCls}>Total Tax</span>
                      <p className="text-[13px] text-brand-dark font-semibold">{details.tax?.amount ? formatPrice(details.tax.amount) : ''}</p>
                    </div>
                    <div />
                  </div>
                </div>
              </div>
            )}

            {/* History tab */}
            {historyTab && (
              <div>
                {/* Table header — sticky, not in scroll area */}
                <div className="flex gap-[16px] py-[4px] bg-white rounded-t-[4px]">
                  <div className="w-[224px] flex items-center pl-[12px]">
                    <span className="text-[14px] font-bold text-brand-dark">Date</span>
                  </div>
                  <div className="w-[224px] flex items-center">
                    <span className="text-[14px] font-bold text-brand-dark"> Status</span>
                  </div>
                  <div className="w-[127px] flex items-center pr-[12px]">
                    <span className="text-[14px] font-bold text-brand-dark">Price</span>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="history-scroll">
                  {sortedYears.length === 0 && (
                    <p className="text-[13px] text-brand-unselectedGray py-[20px] pl-[12px]">No history available.</p>
                  )}
                  {sortedYears.map(year => (
                    <div key={year}>
                      {groupedHistory[year].map((entry, i) => {
                        const Icon = STATUS_ICON_MAP[entry.event_name]
                        const badgeBg = STATUS_BADGE_BG[entry.event_name]
                        return (
                          <div
                            key={i}
                            className="flex gap-[16px] items-center h-[52px] border-b border-brand-rowBorder"
                          >
                            <div className="w-[224px] pl-[12px] py-[2px] shrink-0">
                              <span className="text-[14px] text-brand-dark">{formatHistoryDate(entry.date)}</span>
                            </div>
                            <div className="w-[224px] flex items-center gap-[8px] shrink-0">
                              {Icon && (
                                badgeBg ? (
                                  <div className="size-[20px] rounded-full flex items-center justify-center shrink-0" style={{ background: badgeBg }}>
                                    <Icon />
                                  </div>
                                ) : (
                                  <Icon className="size-[20px] shrink-0" />
                                )
                              )}
                              <span className="text-[14px] text-brand-dark">
                                {STATUS_LABEL_MAP[entry.event_name] ?? entry.event_name ?? ''}
                              </span>
                            </div>
                            <div className="w-[127px] pr-[12px] py-[2px] shrink-0">
                              <span className="text-[14px] text-brand-dark">
                                {entry.price ? formatPrice(entry.price) : ''}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-y-[16px]" style={{ flex: '1.2', minWidth: 0 }}>

            {/* Contacts card */}
            <div className="bg-white border border-[#DCEBD4] rounded-[8px] p-[24px] h-[237px]">

              {/* Card header */}
              <div className="flex items-center justify-between mb-[16px] h-[32px]">
                <span className="text-[15px] font-bold text-brand-dark">Contacts</span>
                {contactEditMode ? (
                  <div className="flex items-center gap-x-[10px]">
                    <button onClick={handleContactDelete} className="hover:opacity-70 transition-opacity">
                      <TrashIcon />
                    </button>
                    <button
                      onClick={cancelContactEdit}
                      style={{ border: '1.5px solid #DAE4D5', borderRadius: 999, padding: '5px 16px', fontSize: 13, fontWeight: 500, background: 'white', cursor: 'pointer', color: '#17270D' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleContactSave}
                      style={{ borderRadius: 999, padding: '5px 16px', fontSize: 13, fontWeight: 600, background: '#4CBC18', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <>
                  <div>
                  {contacts.length > 0 && (
                      <div className="flex items-center gap-x-[10px]">
                        <SliderBackIcon onChange={() => setContactIndex(prev => Math.max(0, prev - 1))} />
                        <span className="text-[12px] text-brand-dark min-w-[28px] text-center">
                          {contactIndex + 1}/{contacts.length}
                        </span>
                        <SliderNextIcon onChange={() => setContactIndex(prev => Math.min(contacts.length - 1, prev + 1))} />
                      </div>
                  )}
                  </div>
                  <div className="flex items-center gap-x-[24px]">
                    <button onClick={openAddContact} className="hover:text-brand-green transition-text">
                      <AddContactIcon />
                    </button>
                    <button
                      onClick={openEditContact}
                      className="flex items-center gap-x-[4px] text-[13px] font-medium text-brand-secondary hover:opacity-70 transition-opacity"
                      disabled={contacts.length === 0}
                      style={{ opacity: contacts.length === 0 ? 0.3 : 1 }}
                    >
                      <EditContactIcon />
                      <span>Edit</span>
                    </button>
                  </div>
                  </>
                )}
              </div>

              {/* View mode */}
              {!contactEditMode && (
                contacts.length === 0 ? (
                  <p className="text-[13px] text-brand-unselectedGray">No contacts yet. Click + to add one.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-x-[16px] gap-y-[14px]">
                    <div>
                      <span className={labelCls}>Name</span>
                      <p className="text-[13px] text-brand-dark h-[40px]">
                        {currentContact?.name || ''}
                        {currentContact?.role && (
                          <span> ({ROLE_OPTIONS.find(o => o.value === currentContact.role)?.label ?? currentContact.role})</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <span className={labelCls}>Mobile #</span>
                      <p className="text-[13px] text-brand-dark">{phoneFormatEval(currentContact?.mobile) || ''}</p>
                    </div>
                    <div>
                      <span className={labelCls}>Email</span>
                      <p className="text-[13px] h-[40px] text-brand-dark">{currentContact?.email || ''}</p>
                    </div>
                    <div>
                      <span className={labelCls}>Other #</span>
                      <p className="text-[13px] text-brand-dark">{phoneFormatEval(currentContact?.other) || ''}</p>
                    </div>
                  </div>
                )
              )}

              {/* Edit mode */}
              {contactEditMode && (
                <div className="grid grid-cols-2 gap-x-[8px] gap-y-[12px]">
                  {/* Row 1 col 1 — Name + Role */}
                  <div>
                    <label className={labelCls}>Name</label>
                    <div
                      className="relative h-[40px] bg-white border border-brand-muted rounded-[8px] flex items-center px-[12px] focus-within:border-brand-green transition-colors hover:border-brand-green"
                    >
                      <input
                        className="flex-1 text-[13px] text-brand-dark outline-none bg-transparent"
                        value={cName}
                        onChange={e => setCName(e.target.value)}
                        placeholder="Full name"
                      />
                      <button
                        type="button"
                        onMouseDown={e => { e.stopPropagation(); setOpenRoleMenu(prev => !prev); }}
                        className="flex items-center gap-[4px] shrink-0 ml-[8px]"
                        style={cRole?.label === 'Attorney' ?{position:'relative',right:'12px'}:{}}
                      >
                        <span className="text-[13px] text-brand-dark" >{cRole?.label || 'Role'}</span>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3.5 5.25l3.5 3.5 3.5-3.5" stroke="#17270D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {openRoleMenu && (
                        <div
                          onMouseDown={e => e.stopPropagation()}
                          className="absolute top-[44px] right-0 z-50 rounded-[8px] p-[14px] flex flex-col gap-[6px] shadow-lg min-w-[120px]"
                          style={{ background: '#17270D' }}
                        >
                          {ROLE_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onMouseDown={e => { e.stopPropagation(); setCRole(opt); setOpenRoleMenu(false); }}
                              className={`text-left text-[12px] text-white px-[6px] py-[4px] rounded-[4px] transition-colors ${
                                cRole?.value === opt.value ? 'bg-[#4f6c3c]' : 'hover:bg-[#4f6c3c]/60'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Row 1 col 2 — Mobile */}
                  <div>
                    <label className={labelCls}>Mobile #</label>
                    <input className={inputCls} value={phoneFormatEval(cMobile) ?? cMobile} onChange={e => setCMobile(e.target.value)}  />
                  </div>
                  {/* Row 2 col 1 — Email */}
                  <div>
                    <label className={labelCls}>Email</label>
                    <input className={inputCls} value={cEmail} onChange={e => setCEmail(e.target.value)} />
                  </div>
                  {/* Row 2 col 2 — Other */}
                  <div>
                    <label className={labelCls}>Other #</label>
                    <input className={inputCls} value={phoneFormatEval(cOther) ?? cOther} onChange={e => setCOther(e.target.value)}  />
                  </div>
                </div>
              )}
            </div>

            {/* Notes + Major Systems card */}
            <div className="bg-white border border-[#DCEBD4] rounded-[8px] p-[24px]">
              <p className="text-[15px] font-bold text-brand-dark mb-[12px]">Notes</p>
              <textarea
                className="w-full text-[13px] border border-brand-muted rounded-[8px] px-[12px] py-[8px] bg-white outline-none hover:border-brand-green focus:border-brand-green resize-none"
                rows={10}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                onBlur={() => {
                  save({ 'notes.text': notes });
                  setNotesModified(new Date().toISOString());
                }}
                placeholder="Add notes…"
              />
              <span className="text-[11px] text-brand-unselectedGray mt-[6px] block h-[16px]">
                {
                  notesModified && `Last Modified: ${formatDate(notesModified)}`
                }
              </span>

              {/* Major Systems */}
              <div className="mt-[20px] pt-[16px]">
                <p className={labelCls}>Major Systems</p>
                <div className="flex items-center gap-x-[12px] mt-[8px]">
                  {[
                    { Icon: RoofIcon, value: roofYear, onChange: setRoofYear, field: 'details.roof' },
                    { Icon: AcIcon, value: acYear, onChange: setAcYear, field: 'details.ac' },
                    { Icon: WaterHeaterIcon, value: whYear, onChange: setWhYear, field: 'details.water_heater' },
                    { Icon: FurnaceIcon, value: furnaceYear, onChange: setFurnaceYear, field: 'details.furnace' },
                  ].map(({ Icon, value, onChange, field }, i) => (
                    <div key={i} className="flex items-center gap-x-[8px]">
                      <Icon />
                      <input
                        type="text"
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        onBlur={() => save({ [field]: value ? Number(value) : null })}
                        className="w-[68px] text-[13px] border border-brand-muted rounded-[8px] px-[8px] py-[6px] bg-white outline-none focus:border-brand-green hover:border-brand-green text-center"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default PropertyDashboard;
